import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../api/auth";
import Particles from 'react-tsparticles';
import { loadSlim } from "tsparticles-slim";
import BackgroundShapes from '../components/common/BackgroundShapes';
import {
    Container, Box, Typography, TextField, Button, Alert, Paper,
    Stack, CircularProgress, InputAdornment, Fade, Link as MuiLink, Avatar,
    LinearProgress, List, ListItem, ListItemIcon, ListItemText, Slide
} from "@mui/material";

// Icons for the page
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// Helper function to check password strength in real-time
const getPasswordStrength = (password) => {
    let score = 0;
    const criteria = {
        length: password.length >= 8,
        upper: /[A-Z]/.test(password),
        lower: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
        special: /[^A-Za-z0-9]/.test(password),
    };

    if (criteria.length) score += 20;
    if (criteria.upper) score += 20;
    if (criteria.lower) score += 20;
    if (criteria.number) score += 20;
    if (criteria.special) score += 20;

    return { score, criteria };
};

export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [token, setToken] = useState(null);
    const [values, setValues] = useState({ password: '', confirmPassword: '' });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [strength, setStrength] = useState({ score: 0, criteria: {} });

    // Configuration for the particle background effect
    const particlesConfig = {
        fullScreen: { enable: false },
        particles: {
            number: { value: 30, density: { enable: true, value_area: 800 } },
            color: { value: ["#43a047", "#ffc400"] },
            shape: { type: "circle" },
            opacity: { value: 0.5, random: true },
            size: { value: 3, random: true },
            move: { enable: true, speed: 1, direction: "none", outModes: "out" }
        },
        interactivity: {
            events: { onHover: { enable: true, mode: "grab" }, onClick: { enable: true, mode: "push" } },
            modes: { grab: { distance: 140 }, push: { quantity: 2 } }
        },
        background: { color: { value: "transparent" } }
    };
    const particlesInit = async (engine) => await loadSlim(engine);

    useEffect(() => {
        const tokenFromUrl = searchParams.get('token');
        if (tokenFromUrl) {
            setToken(tokenFromUrl);
        } else {
            setError("Invalid or missing reset token. Please request a new link.");
        }
    }, [searchParams]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues((prev) => ({ ...prev, [name]: value }));

        if (name === 'password') {
            setStrength(getPasswordStrength(value));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (values.password !== values.confirmPassword) {
            return setError("Passwords do not match.");
        }
        if (strength.score < 80) {
            return setError("Password is not strong enough. Please meet all criteria.");
        }
        setLoading(true);
        setSuccess("");
        try {
            await resetPassword({ token, newPassword: values.password });
            setSuccess("Password has been reset successfully! Redirecting to login...");
            setTimeout(() => navigate("/login"), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to reset password. The link may be invalid or expired.");
        } finally {
            setLoading(false);
        }
    };

    const getStrengthColor = (score) => {
        if (score < 40) return "error";
        if (score < 80) return "warning";
        return "success";
    };

    const passwordCriteria = [
        { key: 'length', text: 'At least 8 characters' },
        { key: 'upper', text: 'An uppercase letter' },
        { key: 'lower', text: 'A lowercase letter' },
        { key: 'number', text: 'A number' },
        { key: 'special', text: 'A special character' },
    ];

    return (
        <Box
            sx={{
                position: 'relative',
                minHeight: 'calc(100vh - 128px)',
                overflow: 'hidden',
                background: `linear-gradient(135deg, #f8faf8, #e6f4ea, #ffecb3)`,
                backgroundSize: '400% 400%',
                animation: 'gradientBG 25s ease infinite',
                '@keyframes gradientBG': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                },
                '@keyframes float': {
                    '0%': { transform: 'translateY(0px) rotate(0deg)' },
                    '50%': { transform: 'translateY(-20px) rotate(10deg)' },
                    '100%': { transform: 'translateY(0px) rotate(0deg)' },
                }
            }}
        >
            <BackgroundShapes />
            <Particles id="tsparticles" init={particlesInit} options={particlesConfig} style={{ position: 'absolute', width: '100%', height: '100%', zIndex: 2 }} />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', py: { xs: 4, md: 8 } }}>
                <Fade in={true} timeout={1200}>
                    <Paper elevation={8} sx={{ p: { xs: 3, sm: 5 }, maxWidth: 500, width: '100%', mx: { xs: 2, md: 0 }, backgroundColor: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(12px)', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Stack alignItems="center" spacing={2}>
                            <Avatar sx={{ mb: 1, bgcolor: 'primary.main', width: 56, height: 56, boxShadow: 3 }}>
                                <VpnKeyOutlinedIcon sx={{ fontSize: 32 }} />
                            </Avatar>
                            <Typography component="h1" variant="h4" fontWeight={700} color="text.primary">
                                Set New Password
                            </Typography>

                            {success && <Alert severity="success" sx={{ width: '100%' }}>{success}</Alert>}
                            {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}

                            {token && !success && (
                                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 1 }}>
                                    <Stack spacing={2}>
                                        <TextField fullWidth label="New Password" name="password" type="password" value={values.password} onChange={handleChange} required InputProps={{ startAdornment: (<InputAdornment position="start"><LockOutlinedIcon sx={{ color: 'text.secondary' }} /></InputAdornment>) }} />

                                        <Box sx={{ width: '100%', pt: 0.5 }}>
                                            <LinearProgress variant="determinate" value={strength.score} color={getStrengthColor(strength.score)} sx={{ height: 8, borderRadius: 4 }} />
                                        </Box>

                                        <TextField fullWidth label="Confirm New Password" name="confirmPassword" type="password" value={values.confirmPassword} onChange={handleChange} required InputProps={{ startAdornment: (<InputAdornment position="start"><LockOutlinedIcon sx={{ color: 'text.secondary' }} /></InputAdornment>) }} />

                                        <List dense sx={{ width: '100%', p: 0, mt: 1 }}>
                                            {passwordCriteria.map((item, index) => (
                                                <Slide direction="right" in={values.password.length > 0} key={item.key} style={{ transitionDelay: `${index * 50}ms` }}>
                                                    <ListItem sx={{ py: 0.25 }}>
                                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                                            {strength.criteria[item.key] ?
                                                                <CheckCircleIcon color="success" sx={{ fontSize: '1rem' }} /> :
                                                                <CancelIcon color="error" sx={{ fontSize: '1rem', opacity: 0.5 }} />
                                                            }
                                                        </ListItemIcon>
                                                        <ListItemText primary={item.text} primaryTypographyProps={{ variant: 'body2', color: strength.criteria[item.key] ? 'text.primary' : 'text.secondary' }} />
                                                    </ListItem>
                                                </Slide>
                                            ))}
                                        </List>

                                        <Button fullWidth type="submit" variant="contained" size="large" disabled={loading} sx={{ py: 1.5, mt: 2 }}>
                                            {loading ? <CircularProgress size={26} color="inherit" /> : 'Reset Password'}
                                        </Button>
                                    </Stack>
                                </Box>
                            )}
                            <MuiLink component={RouterLink} to="/login" variant="body2" sx={{ color: 'text.secondary', pt: 2, fontWeight: 500 }}>
                                &larr; Back to Login
                            </MuiLink>
                        </Stack>
                    </Paper>
                </Fade>
            </Container>
        </Box>
    );
}